import { useEffect, useState } from "react";

import { api, ApiError } from "../api";
import { Art } from "./Art";
import { t } from "../i18n";
import type { Locale } from "../types";

/**
 * Ekran wejściowy pod gołym adresem. Proces normalnie zaczyna się od linku z SMS-a,
 * więc zamiast komunikatu o błędzie tłumaczymy, skąd wziąć link — i pozwalamy go wkleić.
 * Lokalnie dochodzi skrót do założenia testowego onboardingu.
 */
export function Start({ locale }: { locale: Locale }) {
  const [value, setValue] = useState("");
  const [devMode, setDevMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .health()
      // Wprost o tryb instancji, a nie o nazwę bramki SMS: skrót
      // `/api/dev/quickstart` zależy od `DEV_MODE`, nie od dostawcy wiadomości.
      .then((health) => setDevMode(Boolean(health.dev_mode)))
      .catch(() => setDevMode(false));
  }, []);

  const open = () => {
    const found = value.trim().match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (!found) {
      setError(t("startBadId", locale));
      return;
    }
    window.location.href = `/o/${found[1]}`;
  };

  const quickstart = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.quickstart();
      window.location.href = `/o/${created.record_id}`;
    } catch (caught) {
      setError((caught as ApiError).message);
      setBusy(false);
    }
  };

  return (
    <div className="gate gate--art">
      <Art name="start-link" size={168} className="gate__art" />
      <h1 className="gate__title">{t("startTitle", locale)}</h1>
      <p className="gate__text">{t("startText", locale)}</p>

      <div className="gate__row">
        <input
          className="input"
          type="text"
          inputMode="url"
          placeholder={t("startPlaceholder", locale)}
          aria-label={t("startPlaceholder", locale)}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && open()}
        />
        <button className="btn btn--primary" onClick={open} disabled={!value.trim()}>
          {t("startOpen", locale)}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {devMode && (
        <>
          <p className="field__hint" style={{ marginTop: 22 }}>
            {t("startDevHint", locale)}
          </p>
          <button className="btn btn--secondary btn--wide" onClick={quickstart} disabled={busy}>
            {busy ? t("saving", locale) : t("startDevButton", locale)}
          </button>
        </>
      )}
    </div>
  );
}
